import { getConfig } from '@cian/config/browser';
import { getLogger } from '@cian/logger/browser';

import {
  classifySource,
  getCulprit,
  getEntryType,
  getHeaderHeight,
  getRootHeight,
  getSessionInfo,
  serializeSources,
  type ILayoutShift,
  type IOwnershipSplit,
} from './helpers';

interface IClsDebugParams {
  /** Из стора: авторизован ли посетитель и в какой он A/B-группе. */
  isAuthenticated: boolean;
  abGroup: number | null;
}

/**
 * Временная диагностика CLS: пишет в ELK атрибуцию сдвига по каждой загрузке.
 * Нужна, чтобы понять, что именно двигается у сессий с rating=poor, и главное —
 * приходит сдвиг из нашей разметки или из шапки/футера, которые выкатываются отдельно.
 *
 * Удалить после того, как причина найдена.
 */

/** Доля хороших сессий, которые тоже логируем — чтобы знать распределение, а не только хвост. */
const DEFAULT_GOOD_SAMPLE_RATE = 0.05;

/**
 * Считает сдвиги параллельно с web-vitals: тот отдаёт только крупнейший сдвиг,
 * а нам нужна разбивка по владельцу разметки за всю сессию.
 */
function observeOwnership(): IOwnershipSplit {
  const split: IOwnershipSplit = {
    ours: 0,
    rootMoved: 0,
    foreign: 0,
    unknown: 0,
    scrollYAtWorst: 0,
    worstValue: 0,
    worstSources: [],
    headerHeightStart: getHeaderHeight(),
    headerHeightEnd: null,
  };

  const observer = new PerformanceObserver(list => {
    for (const entry of list.getEntries() as ILayoutShift[]) {
      if (entry.hadRecentInput) {
        continue;
      }

      const sources = entry.sources || [];
      const owners = sources.map(source => classifySource(source.node));

      if (sources.length === 0) {
        split.unknown += entry.value;
      } else if (owners.includes('rootMoved')) {
        /* Корень уехал целиком — причина снаружи, что бы ещё ни было в источниках. */
        split.rootMoved += entry.value;
      } else if (owners.includes('ours')) {
        split.ours += entry.value;
      } else {
        split.foreign += entry.value;
      }

      if (entry.value > split.worstValue) {
        split.worstValue = entry.value;
        split.scrollYAtWorst = Math.round(window.scrollY);
        split.worstSources = serializeSources(sources);
      }
    }
  });

  observer.observe({ type: 'layout-shift', buffered: true } as PerformanceObserverInit);

  return split;
}

export function initClsDebug(params: IClsDebugParams): void {
  try {
    const config = getConfig();

    if (config.get<boolean>('realtorReviews.clsDebug.enabled') === false) {
      return;
    }

    const goodSampleRate = config.get<number>('realtorReviews.clsDebug.goodSampleRate') ?? DEFAULT_GOOD_SAMPLE_RATE;

    let maxScrollY = 0;
    const trackScroll = () => {
      const y = Math.round(window.scrollY);
      if (y > maxScrollY) {
        maxScrollY = y;
      }
    };
    window.addEventListener('scroll', trackScroll, { passive: true });

    const split = observeOwnership();
    const rootHeightStart = getRootHeight();

    void import('web-vitals/attribution')
      .then(({ onCLS }) => {
        onCLS(metric => {
          try {
            if (metric.rating === 'good' && Math.random() > goodSampleRate) {
              return;
            }

            const a = (metric.attribution || {}) as Record<string, unknown>;
            const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
            const round = (v: unknown, d = 4) => (typeof v === 'number' ? Number(v.toFixed(d)) : null);

            split.headerHeightEnd = getHeaderHeight();

            const navType = nav ? nav.type : null;

            getLogger().warning('cwv_cls_debug', {
              /* Три поля, ради которых всё затевалось: сколько, какой статус, кто. */
              cls: round(metric.value),
              rating: metric.rating,
              culprit: getCulprit(split),

              /**
               * Готовые категории для фильтрации: числовые сравнения по коллекции
               * key/value в ELK ненадёжны, а по строке фильтр работает всегда.
               */
              entryType: getEntryType(navType),
              viewportBucket: window.innerWidth < 1025 ? 'narrow' : 'wide',

              target: (a.largestShiftTarget as string) || null,

              /**
               * Кто именно виноват. `target` выше — это пострадавший, его сдвинули.
               * Виновника ищем здесь по `dh`: у кого изменилась высота, тот и раздвинул вёрстку.
               * У кого только `dy` — того просто отнесло.
               */
              worstSources: split.worstSources,

              /**
               * Схлопнулась ли шапка после гидратации. Если Start и End отличаются —
               * причина снаружи, и никакая правка в нашем репозитории её не уберёт.
               */
              headerHeightStart: split.headerHeightStart,
              headerHeightEnd: split.headerHeightEnd,

              shiftValue: round(a.largestShiftValue),
              shiftTime: round(a.largestShiftTime, 0),
              loadState: (a.loadState as string) || null,
              shifts: metric.entries.length,

              /* Кто двигал вёрстку. rootMoved — корень толкнуло снаружи, мы пострадавший. */
              clsOurs: round(split.ours),
              clsRootMoved: round(split.rootMoved),
              clsForeign: round(split.foreign),
              clsUnknown: round(split.unknown),

              /* Где стоял экран в момент худшего сдвига, а не в момент отправки. */
              scrollYAtWorst: split.scrollYAtWorst,
              maxScrollY,
              scrollYNow: Math.round(window.scrollY),

              /* Ширина решает: ui-kit переключает вёрстку на 1025 px. */
              viewportW: window.innerWidth,
              viewportH: window.innerHeight,
              dpr: window.devicePixelRatio,

              /* Наша часть выросла или сжалась — прямое измерение вместо выводов по соседям. */
              rootHeightStart,
              rootHeightEnd: getRootHeight(),

              /**
               * Состояние сессии. Разделяет «медленный канал», «слабое железо»
               * и «залогинен» — три версии, которые иначе не различить.
               */
              isAuthenticated: params.isAuthenticated,
              abGroup: params.abGroup,
              ...getSessionInfo(),

              /* Как пришли: якорь и тип навигации объясняют позицию экрана к моменту гидратации. */
              hash: window.location.hash || null,
              path: window.location.pathname,
              navType,
              ttfb: nav ? Math.round(nav.responseStart) : null,
            });
          } catch {
            /* диагностика не должна ломать страницу */
          }
        });
      })
      .catch(() => undefined);
  } catch {
    /* диагностика не должна ломать страницу */
  }
}
